import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
  AiPriceTable,
  AnswerValue,
  ApplicationBoard,
  ApplicationDetail,
  ApplicationEvent,
  ApplicationListItem,
  ApplicationPatch,
  ApplicationStats,
  ApplicationStatus,
  AuthTokens,
  BoardListParams,
  BoardPatch,
  BoardReadResult,
  CanonicalQuestion,
  Certification,
  CommonQuestion,
  CostOverview,
  Education,
  EmailTrackingMode,
  EmailTrackingSettings,
  EmailTrackingSetup,
  Experience,
  FormTemplate,
  InboundMessage,
  InboundMessageList,
  IngestRunResult,
  IngestSettings,
  IngestSettingsPatch,
  Job,
  JobDetail,
  JobFacets,
  JobFilters,
  JobSourcesOverview,
  KeyConfirmation,
  LoginCredentials,
  MailClassification,
  ModelOptions,
  ModelPreference,
  ModelPrompt,
  ModelPrompts,
  NewBoard,
  PaginatedResponse,
  PlanSettings,
  PlanState,
  Profile,
  ProfilePatch,
  ProfileSnapshot,
  ProviderKeys,
  RegisterData,
  Resume,
  SavedSearch,
  SavedSearchAlert,
  ServiceCost,
  ServiceCostInput,
  Skill,
  SourceBoard,
  JobMatch,
  Library,
  LibraryEntry,
  TailoredEdit,
  TailoredVersion,
  User,
  UserAnswer,
  VendorModel,
} from '../types';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './tokenStorage';

// API base URL - adjust based on your backend port
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

/**
 * Event the auth store listens for so it can drop to a logged-out state the
 * moment a session dies, rather than waiting for the next reload.
 */
export const AUTH_EXPIRED_EVENT = 'pathfindr:auth-expired';

/**
 * Event fired (with the new token pair in `detail`) whenever the interceptor
 * rotates tokens, so the zustand store can mirror them into its persisted
 * state without api.ts importing the store (which would be a cycle).
 */
export const TOKENS_REFRESHED_EVENT = 'pathfindr:tokens-refreshed';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Wipe the zustand-persisted auth snapshot without importing the store.
 * The store also listens for AUTH_EXPIRED_EVENT to clear its in-memory state.
 */
function clearPersistedAuth(): void {
  try {
    localStorage.removeItem('pathfindr-auth');
  } catch {
    /* ignore storage errors */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
}

// ---------------------------------------------------------------------------
// Single-flight refresh
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<AuthTokens> | null = null;

/**
 * Exchange the stored refresh token for a new pair. Concurrent callers share
 * one in-flight request so a rotated (and therefore blacklisted) refresh token
 * is never sent twice.
 */
export function refreshTokens(): Promise<AuthTokens> {
  if (!refreshInFlight) {
    const refresh = getRefreshToken();
    if (!refresh) {
      return Promise.reject(new Error('No refresh token'));
    }
    // Bare axios (not `api`) so this request skips the interceptors and
    // cannot re-enter the 401 handler.
    refreshInFlight = axios
      .post(`${API_BASE_URL}/auth/token/refresh/`, { refresh })
      .then((response) => {
        const tokens: AuthTokens = {
          access: response.data.access,
          // Rotation is on; the server returns the new refresh token. Fall
          // back to the old one only if the server did not rotate.
          refresh: response.data.refresh || refresh,
        };
        setTokens(tokens);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(TOKENS_REFRESHED_EVENT, { detail: tokens }));
        }
        return tokens;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// Endpoints whose 401 means "bad input", not "expired session".
const NO_REFRESH_PATHS = ['/auth/login/', '/auth/register/', '/auth/google/', '/auth/token/refresh/'];

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean };

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const url = originalRequest?.url ?? '';
    const skip = NO_REFRESH_PATHS.some((p) => url.includes(p));

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !skip) {
      originalRequest._retry = true;

      if (!getRefreshToken()) {
        // No way to recover -- clear and let the app route back to login.
        clearTokens();
        clearPersistedAuth();
        return Promise.reject(error);
      }

      try {
        const tokens = await refreshTokens();
        originalRequest.headers = {
          ...(originalRequest.headers ?? {}),
          Authorization: `Bearer ${tokens.access}`,
        };
        // Logout sends the refresh token in its body; after rotation the old
        // one is blacklisted, so swap in the new one before retrying.
        if (url.includes('/auth/logout/')) {
          originalRequest.data = JSON.stringify({ refresh_token: tokens.refresh });
        }
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed -- the session is dead. Clear everything so
        // ProtectedRoute sends the user to login on the next render.
        clearTokens();
        clearPersistedAuth();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post('/auth/login/', credentials);
    return response.data;
  },

  register: async (userData: RegisterData): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post('/auth/register/', userData);
    return response.data;
  },

  /**
   * Sign in (200) or create the account (201) from a Google ID token. Same
   * response as `login`; a rejected token is a 400 with `detail`.
   */
  google: async (credential: string): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post('/auth/google/', { credential });
    return response.data;
  },

  /** Blacklists the refresh token server-side. Body key is `refresh_token`. */
  /** Deletes the account and everything it holds. Needs the person to prove again who they are. */
  deleteAccount: async (confirmation: KeyConfirmation): Promise<void> => {
    await api.delete('/auth/account/', { data: confirmation, timeout: 60000 });
  },

  logout: async (refreshToken: string): Promise<void> => {
    await api.post('/auth/logout/', { refresh_token: refreshToken });
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  /**
   * A separate token pair for the browser extension. Refresh tokens rotate,
   * so the extension must never share the dashboard's own pair.
   */
  mintExtensionTokens: async (): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post('/auth/extension-token/');
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export const jobsApi = {
  /** How the signed-in person matches a job. The first look at a posting reads it, which takes a few seconds. */
  getMatch: async (id: string, signal?: AbortSignal): Promise<JobMatch> => {
    const response = await api.get(`/jobs/${encodeURIComponent(id)}/match/`, { signal, timeout: 60000 });
    return response.data;
  },

  /** One page of the job board. Pass a signal to cancel a stale request. */
  getJobs: async (params?: JobFilters, signal?: AbortSignal): Promise<PaginatedResponse<Job>> => {
    const response = await api.get('/jobs/', { params, signal });
    return response.data;
  },

  /**
   * Counts per filter option for the same filters as the list. `sort`, `page`
   * and `page_size` do not affect the counts, so leave them out.
   */
  getFacets: async (params?: JobFilters, signal?: AbortSignal): Promise<JobFacets> => {
    // Counting every group over the whole board is slower than a list page.
    const response = await api.get('/jobs/facets/', { params, signal, timeout: 30000 });
    return response.data;
  },

  /** Full job, with the description read live from the ATS (plain text). */
  getJob: async (id: string, signal?: AbortSignal): Promise<JobDetail> => {
    // The API reads the description from the ATS on a cache miss.
    const response = await api.get(`/jobs/${encodeURIComponent(id)}/`, { signal, timeout: 30000 });
    return response.data;
  },

  getJobStats: async () => {
    const response = await api.get('/jobs/stats/');
    return response.data;
  },

  getTrendingJobs: async () => {
    const response = await api.get('/jobs/trending/');
    return response.data;
  },

  getRecommendedJobs: async () => {
    const response = await api.get('/jobs/recommended/');
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Job sources (staff only)
// ---------------------------------------------------------------------------

// These read boards from the ATS while the request waits, so they outlast the
// default timeout: a run takes up to about 40 seconds and one board about 20.
const RUN_TIMEOUT_MS = 120000;
const BOARD_READ_TIMEOUT_MS = 60000;

export const jobSourcesApi = {
  overview: async (signal?: AbortSignal): Promise<JobSourcesOverview> => {
    const response = await api.get('/jobs/admin/overview/', { signal, timeout: 30000 });
    return response.data;
  },

  /** Any subset of the writable settings; returns all of them. */
  patchSettings: async (data: IngestSettingsPatch): Promise<IngestSettings> => {
    const response = await api.patch('/jobs/admin/settings/', data);
    return response.data;
  },

  boards: async (params?: BoardListParams, signal?: AbortSignal): Promise<PaginatedResponse<SourceBoard>> => {
    const response = await api.get('/jobs/admin/boards/', { params, signal, timeout: 30000 });
    return response.data;
  },

  /** Registers the board and reads it at once; 400 `{token: [...]}` when the ATS has no such board. */
  addBoard: async (data: NewBoard): Promise<{ board: SourceBoard; result: BoardReadResult | null }> => {
    const response = await api.post('/jobs/admin/boards/', data, { timeout: BOARD_READ_TIMEOUT_MS });
    return response.data;
  },

  patchBoard: async (id: string, data: BoardPatch): Promise<{ board: SourceBoard; jobs_hidden: number }> => {
    const response = await api.patch(`/jobs/admin/boards/${encodeURIComponent(id)}/`, data);
    return response.data;
  },

  /** Flags the board's company as a staffing agency, or clears the flag. A manual decision: later refreshes keep it. */
  setBoardAgency: async (id: string, isStaffingAgency: boolean): Promise<{ board: SourceBoard; jobs_hidden: number }> => {
    const data: BoardPatch = { is_staffing_agency: isStaffingAgency };
    const response = await api.patch(`/jobs/admin/boards/${encodeURIComponent(id)}/`, data);
    return response.data;
  },

  /** Reads one board now, switching it on first if it was off. */
  refreshBoard: async (id: string): Promise<{ board: SourceBoard; result: BoardReadResult }> => {
    const response = await api.post(`/jobs/admin/boards/${encodeURIComponent(id)}/refresh/`, undefined, {
      timeout: BOARD_READ_TIMEOUT_MS,
    });
    return response.data;
  },

  removeBoard: async (id: string): Promise<{ jobs_hidden: number }> => {
    const response = await api.delete(`/jobs/admin/boards/${encodeURIComponent(id)}/`);
    return response.data;
  },

  /** One refresh call. `all` re-reads every active board instead of the due ones. */
  run: async (data?: { all?: boolean; before?: string; platforms?: string[] }): Promise<IngestRunResult> => {
    const response = await api.post('/jobs/admin/run/', data ?? {}, { timeout: RUN_TIMEOUT_MS });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

function crud<T extends { id: number | string }>(base: string) {
  return {
    list: async (): Promise<T[]> => {
      const response = await api.get(base);
      return Array.isArray(response.data) ? response.data : response.data.results ?? [];
    },
    create: async (data: Partial<T>): Promise<T> => {
      const response = await api.post(base, data);
      return response.data;
    },
    update: async (id: number | string, data: Partial<T>): Promise<T> => {
      const response = await api.patch(`${base}${id}/`, data);
      return response.data;
    },
    remove: async (id: number | string): Promise<void> => {
      await api.delete(`${base}${id}/`);
    },
  };
}

export const profileApi = {
  get: async (): Promise<Profile> => {
    const response = await api.get('/profile/');
    return response.data;
  },
  patch: async (data: ProfilePatch): Promise<Profile> => {
    const response = await api.patch('/profile/', data);
    return response.data;
  },
  snapshot: async (): Promise<ProfileSnapshot> => {
    const response = await api.get('/profile/snapshot/');
    return response.data;
  },
  education: crud<Education>('/profile/education/'),
  experience: crud<Experience>('/profile/experience/'),
  skills: crud<Skill>('/profile/skills/'),
  certifications: crud<Certification>('/profile/certifications/'),
};

// ---------------------------------------------------------------------------
// Answers (canonical keyed answers)
// ---------------------------------------------------------------------------

export const answersApi = {
  questions: async (): Promise<CanonicalQuestion[]> => {
    const response = await api.get('/answers/questions/');
    return response.data;
  },
  list: async (): Promise<UserAnswer[]> => {
    const response = await api.get('/answers/');
    return response.data;
  },
  put: async (
    questionKey: string,
    data: { value: AnswerValue; verified: boolean }
  ): Promise<UserAnswer> => {
    const response = await api.put(`/answers/${encodeURIComponent(questionKey)}/`, data);
    return response.data;
  },
  remove: async (questionKey: string): Promise<void> => {
    await api.delete(`/answers/${encodeURIComponent(questionKey)}/`);
  },
  /** The questions employers ask most across the job board, unanswered first. */
  common: async (limit = 40, signal?: AbortSignal): Promise<CommonQuestion[]> => {
    const response = await api.get('/answers/common/', { params: { limit }, signal });
    return Array.isArray(response.data?.results) ? response.data.results : [];
  },
};

// ---------------------------------------------------------------------------
// Templates (free-text custom answers, category `custom_response`)
// ---------------------------------------------------------------------------

export const templatesApi = {
  /** Unpaginated list, optionally filtered by category. */
  list: async (category?: string): Promise<FormTemplate[]> => {
    const response = await api.get('/templates/list/', {
      params: category ? { category } : undefined,
    });
    // Tolerate a paginated envelope in case the backend is not updated yet.
    return Array.isArray(response.data) ? response.data : response.data.results ?? [];
  },

  create: async (data: {
    category: string;
    question: string;
    value: string;
  }): Promise<FormTemplate> => {
    const response = await api.post('/templates/', data);
    return response.data;
  },

  patch: async (
    id: number,
    data: Partial<{ question: string; value: string; category: string }>
  ): Promise<FormTemplate> => {
    const response = await api.patch(`/templates/${id}/`, data);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/templates/${id}/delete/`);
  },
};

// ---------------------------------------------------------------------------
// Resumes
// ---------------------------------------------------------------------------

function filenameFromDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

export const resumesApi = {
  list: async (): Promise<Resume[]> => {
    const response = await api.get('/resumes/');
    return Array.isArray(response.data) ? response.data : response.data.results ?? [];
  },

  upload: async (
    file: File,
    options?: { label?: string; is_default?: boolean; onProgress?: (percent: number) => void }
  ): Promise<Resume> => {
    const form = new FormData();
    form.append('file', file);
    if (options?.label) form.append('label', options.label);
    if (options?.is_default !== undefined) form.append('is_default', String(options.is_default));
    const response = await api.post('/resumes/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (evt) => {
        if (options?.onProgress && evt.total) {
          options.onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },

  patch: async (id: string, data: Partial<{ label: string; is_default: boolean }>): Promise<Resume> => {
    const response = await api.patch(`/resumes/${id}/`, data);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/resumes/${id}/`);
  },

  /** Path the extension and the download helper use. */
  downloadUrl: (id: string): string => `/resumes/${id}/download/`,

  /**
   * Fetch the file with the bearer token and trigger a browser download.
   * No public media URL is ever exposed, so a plain <a href> would 401.
   */
  download: async (id: string, fallbackFilename: string): Promise<void> => {
    const response = await api.get(resumesApi.downloadUrl(id), {
      responseType: 'blob',
      timeout: 120000,
    });
    const filename =
      filenameFromDisposition(response.headers?.['content-disposition']) || fallbackFilename;
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },
};

// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------

export const applicationsApi = {
  list: async (status?: ApplicationStatus | ''): Promise<ApplicationListItem[]> => {
    const response = await api.get('/applications/', {
      params: status ? { status } : undefined,
    });
    return Array.isArray(response.data) ? response.data : response.data.results ?? [];
  },

  get: async (id: string): Promise<ApplicationDetail> => {
    const response = await api.get(`/applications/${id}/`);
    return response.data;
  },

  /** Save a listed job: creates the application with status `saved`. */
  create: async (jobId: string): Promise<ApplicationDetail> => {
    const response = await api.post('/applications/create/', { job_id: jobId });
    return response.data;
  },

  patch: async (id: string, data: ApplicationPatch): Promise<ApplicationDetail> => {
    const response = await api.patch(`/applications/${id}/`, data);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/applications/${id}/`);
  },

  board: async (): Promise<ApplicationBoard> => {
    const response = await api.get('/applications/board/');
    return response.data;
  },

  stats: async (): Promise<ApplicationStats> => {
    const response = await api.get('/applications/stats/');
    return response.data;
  },

  addEvent: async (
    id: string,
    data: { event_type: string; description: string }
  ): Promise<ApplicationEvent> => {
    const response = await api.post(`/applications/${id}/events/`, data);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Staff: costs
// ---------------------------------------------------------------------------

export const opsApi = {
  /** `refresh` asks AWS and the database provider again instead of using the cached answer. */
  costs: async (refresh = false, signal?: AbortSignal): Promise<CostOverview> => {
    const response = await api.get('/ops/costs/', { params: refresh ? { refresh: 1 } : undefined, signal, timeout: 30000 });
    return response.data;
  },

  aiPrices: async (signal?: AbortSignal): Promise<AiPriceTable> => {
    const response = await api.get('/ops/costs/ai-prices/', { signal });
    return response.data;
  },

  services: async (signal?: AbortSignal): Promise<ServiceCost[]> => {
    const response = await api.get('/ops/costs/services/', { signal });
    return response.data;
  },

  addService: async (data: ServiceCostInput): Promise<ServiceCost> => {
    const response = await api.post('/ops/costs/services/', data);
    return response.data;
  },

  updateService: async (id: number, data: Partial<ServiceCostInput>): Promise<ServiceCost> => {
    const response = await api.patch(`/ops/costs/services/${id}/`, data);
    return response.data;
  },

  removeService: async (id: number): Promise<void> => {
    await api.delete(`/ops/costs/services/${id}/`);
  },

  /** Which provider keys are set. Never the keys. */
  keys: async (signal?: AbortSignal): Promise<ProviderKeys> => {
    const response = await api.get('/ops/keys/', { signal, timeout: 30000 });
    return response.data;
  },

  /** Checks the key with its service, then stores it. Needs the owner to confirm who they are. */
  setKey: async (name: string, value: string, confirmation: KeyConfirmation): Promise<void> => {
    await api.put(`/ops/keys/${encodeURIComponent(name)}/`, { value, ...confirmation }, { timeout: 30000 });
  },

  clearKey: async (name: string, confirmation: KeyConfirmation): Promise<void> => {
    await api.delete(`/ops/keys/${encodeURIComponent(name)}/`, { data: confirmation, timeout: 30000 });
  },

  /** The instructions given to the models, and whether each is the built-in text. */
  prompts: async (signal?: AbortSignal): Promise<ModelPrompts> => {
    const response = await api.get('/ops/prompts/', { signal, timeout: 30000 });
    return response.data;
  },

  savePrompt: async (name: string, text: string): Promise<ModelPrompt> => {
    const response = await api.put(`/ops/prompts/${encodeURIComponent(name)}/`, { text }, { timeout: 30000 });
    return response.data;
  },

  /** Back to the built-in text. */
  resetPrompt: async (name: string): Promise<ModelPrompt> => {
    const response = await api.delete(`/ops/prompts/${encodeURIComponent(name)}/`, { timeout: 30000 });
    return response.data;
  },

  /** The numbers of the plans: Pro's prices, the allowances, the trial. */
  planSettings: async (signal?: AbortSignal): Promise<PlanSettings> => {
    const response = await api.get('/ops/plans/', { signal });
    return response.data;
  },

  savePlanSettings: async (data: Partial<Record<'pro_price_monthly' | 'pro_price_yearly', string | null> & Record<'pro_tailored_per_month' | 'trial_days' | 'trial_tailored', number>>): Promise<PlanSettings> => {
    const response = await api.put('/ops/plans/', data);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Application status tracking by email
// ---------------------------------------------------------------------------

export const mailApi = {
  settings: async (signal?: AbortSignal): Promise<EmailTrackingSettings> => {
    const response = await api.get('/mail/settings/', { signal });
    return response.data;
  },

  updateSettings: async (data: { mode?: EmailTrackingMode }): Promise<EmailTrackingSettings> => {
    const response = await api.patch('/mail/settings/', data);
    return response.data;
  },

  setup: async (signal?: AbortSignal): Promise<EmailTrackingSetup> => {
    const response = await api.get('/mail/setup/', { signal });
    return response.data;
  },

  messages: async (params?: { application?: string; unmatched?: 1 }, signal?: AbortSignal): Promise<InboundMessageList> => {
    const response = await api.get('/mail/messages/', { params, signal });
    return response.data;
  },

  /** Say which application a message is about (null: none) and/or correct what it says. */
  updateMessage: async (id: string, data: { application?: string | null; classification?: MailClassification }): Promise<InboundMessage> => {
    const response = await api.patch(`/mail/messages/${encodeURIComponent(id)}/`, data);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Language models for tailored resumes
// ---------------------------------------------------------------------------

export const modelsApi = {
  options: async (signal?: AbortSignal): Promise<ModelOptions> => {
    const response = await api.get('/ai/options/', { signal, timeout: 30000 });
    return response.data;
  },

  /** Checks the key with the vendor before storing it; the key is never returned. */
  saveKey: async (vendor: string, key: string): Promise<{ vendor: string; last4: string; models: number }> => {
    const response = await api.put(`/ai/keys/${encodeURIComponent(vendor)}/`, { key }, { timeout: 30000 });
    return response.data;
  },

  removeKey: async (vendor: string): Promise<void> => {
    await api.delete(`/ai/keys/${encodeURIComponent(vendor)}/`);
  },

  /** The vendor's models as it lists them now, with prices and this user's cost per resume. */
  vendorModels: async (vendor: string, signal?: AbortSignal): Promise<{ vendor: string; models: VendorModel[] }> => {
    const response = await api.get('/ai/models/', { params: { vendor }, signal, timeout: 30000 });
    return response.data;
  },

  setPreference: async (data: { source: 'jobflo' } | { source: 'own'; vendor: string; model: string }): Promise<ModelPreference> => {
    const response = await api.patch('/ai/preference/', data);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Tailored resumes
// ---------------------------------------------------------------------------

/** The Experience library: what a person has done beyond their resume, which tailoring also draws on. */
export const libraryApi = {
  list: async (signal?: AbortSignal): Promise<Library> => {
    const response = await api.get('/resumes/library/', { signal });
    return response.data;
  },

  get: async (id: string): Promise<LibraryEntry> => {
    const response = await api.get(`/resumes/library/${encodeURIComponent(id)}/`);
    return response.data;
  },

  /** Something written down: one thing done per line. */
  addNote: async (data: { text: string; title?: string; employer?: string }): Promise<LibraryEntry> => {
    const response = await api.post('/resumes/library/', data);
    return response.data;
  },

  /** A document: its text is kept, the file is not. */
  addDocument: async (file: File, data: { title?: string; employer?: string }): Promise<LibraryEntry> => {
    const form = new FormData();
    form.append('file', file);
    if (data.title) form.append('title', data.title);
    if (data.employer) form.append('employer', data.employer);
    const response = await api.post('/resumes/library/', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
    return response.data;
  },

  update: async (id: string, data: { title?: string; employer?: string; text?: string }): Promise<LibraryEntry> => {
    const response = await api.patch(`/resumes/library/${encodeURIComponent(id)}/`, data);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/resumes/library/${encodeURIComponent(id)}/`);
  },
};

export const tailoredApi = {
  list: async (params?: { application?: string; job?: string }, signal?: AbortSignal): Promise<TailoredVersion[]> => {
    const response = await api.get('/resumes/tailored/', { params, signal });
    return response.data;
  },

  get: async (id: string, signal?: AbortSignal): Promise<TailoredVersion> => {
    const response = await api.get(`/resumes/tailored/${encodeURIComponent(id)}/`, { signal });
    return response.data;
  },

  /** The person's own changes; the PDF is made again and the version comes back as it now is. */
  update: async (id: string, edit: TailoredEdit): Promise<TailoredVersion> => {
    const response = await api.patch(`/resumes/tailored/${encodeURIComponent(id)}/`, edit, { timeout: 60000 });
    return response.data;
  },

  /** Starts (or finds) the tailored resume for a job on the board; poll `get` until it is ready or failed. */
  start: async (data: { job: string; resume?: string }): Promise<TailoredVersion> => {
    const response = await api.post('/resumes/tailored/', data, { timeout: 30000 });
    return response.data;
  },

  /** The PDF as an object URL for showing in the page; the caller revokes it when done. */
  fileUrl: async (version: TailoredVersion): Promise<string> => {
    const response = await api.get(`/resumes/tailored/${encodeURIComponent(version.id)}/download/`, { responseType: 'blob', timeout: 120000 });
    return URL.createObjectURL(new Blob([response.data as Blob], { type: 'application/pdf' }));
  },

  download: async (version: TailoredVersion): Promise<void> => {
    const response = await api.get(`/resumes/tailored/${encodeURIComponent(version.id)}/download/`, { responseType: 'blob', timeout: 120000 });
    const filename = filenameFromDisposition(response.headers?.['content-disposition']) || version.filename || 'resume.pdf';
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  /** Removes a version and its file. */
  remove: async (id: string): Promise<void> => {
    await api.delete(`/resumes/tailored/${encodeURIComponent(id)}/`);
  },
};

/** The plan the user is on and what it allows. */
export const plansApi = {
  current: async (signal?: AbortSignal): Promise<PlanState> => {
    const response = await api.get('/subscriptions/plan/', { signal });
    return response.data;
  },
};

/** Job board searches the user saved. */
export const savedSearchesApi = {
  list: async (signal?: AbortSignal): Promise<SavedSearch[]> => {
    const response = await api.get('/jobs/searches/', { signal });
    return response.data;
  },

  /** Saves a search; saving the same query again renames the existing one. */
  save: async (name: string, query: string, alert: SavedSearchAlert): Promise<SavedSearch> => {
    const response = await api.post('/jobs/searches/', { name, query, alert });
    return response.data;
  },

  /** Rename it, or change whether its new matches are emailed. */
  update: async (id: string, changes: { name?: string; alert?: SavedSearchAlert }): Promise<SavedSearch> => {
    const response = await api.patch(`/jobs/searches/${encodeURIComponent(id)}/`, changes);
    return response.data;
  },

  /** Marks a search as used, so it moves to the front of the list. */
  used: async (id: string): Promise<SavedSearch> => {
    const response = await api.post(`/jobs/searches/${encodeURIComponent(id)}/`);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/jobs/searches/${encodeURIComponent(id)}/`);
  },
};

export default api;
